import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  Button,
  Card,
  CardContent,
  Container,
  Link as MuiLink,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useMarketingRouterRules } from '~/hooks/apis/promo/useMarketingRouterRules';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterInternalQuery } from '~/services/PromoService';

import { isoToSydneyDate } from '~/utils/dateFormatter';

import MarketingRouterResolverTab from './MarketingRouterResolverTab';
import MarketingRouterRuleTableRow, {
  MARKETING_ROUTER_RULE_TABLE_COLUMNS,
  MarketingRouterRuleTableHead,
} from './MarketingRouterRuleTableRow';
import MarketingRouterRulesFilterBar from './MarketingRouterRulesFilterBar';
import {
  MAX_COMPARED_RULES,
  MIN_COMPARED_RULES,
  parseComparisonRuleIds,
  toComparisonPath,
  withComparisonSelection,
} from './marketingRouterComparison';
import {
  EMPTY_MARKETING_ROUTER_RULE_FILTERS,
  type MarketingRouterRuleFilters,
  filterMarketingRouterRules,
  hasActiveMarketingRouterRuleFilters,
  parseMarketingRouterRuleFilters,
  toMarketingRouterRuleFilterParams,
} from './marketingRouterRuleFilters';

const RULES_PATH = '/marketing/marketing-router-rules';
const TAB_PARAM = 'tab';
type RulesPageTab = 'rules' | 'resolve';

export default function MarketingRouterRulesPage() {
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rules = rulesQuery.data ?? [];
  const sortedRules = useMemo(() => [...rules].sort((first, second) => first.id.localeCompare(second.id)), [rules]);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Filters live in the URL so a filtered view can be shared and survives a
  // reload; the page holds no filter state of its own.
  const [searchParams, setSearchParams] = useSearchParams();
  // The tab lives in the URL so a resolved URL can be linked. Everything else
  // in the query string is carried across, so filters survive a look at the
  // resolver and a resolved URL survives a look at the rules.
  const tab: RulesPageTab = searchParams.get(TAB_PARAM) === 'resolve' ? 'resolve' : 'rules';
  const tabHref = (next: RulesPageTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'rules') {
      params.delete(TAB_PARAM);
    } else {
      params.set(TAB_PARAM, next);
    }
    const queryString = params.toString();
    return { pathname: RULES_PATH, search: queryString ? `?${queryString}` : '' };
  };
  const filters = useMemo(() => parseMarketingRouterRuleFilters(searchParams), [searchParams]);
  const hasFilters = hasActiveMarketingRouterRuleFilters(filters);
  // Rule dates are Sydney calendar days, so "today" has to be one too.
  const today = useMemo(() => isoToSydneyDate(new Date().toISOString()), []);
  const shownRules = useMemo(
    () => filterMarketingRouterRules(sortedRules, filters, today),
    [sortedRules, filters, today],
  );
  const regionCodes = useMemo(
    () =>
      getRegions(tenant.brand)
        .map((region) => region.code)
        .sort(),
    [tenant.brand],
  );
  // The selection lives in the URL beside the filters, so coming back from a
  // comparison returns with it still ticked and a comparison can be adjusted
  // by one rule instead of rebuilt. Selection order is kept: it becomes the
  // column order there, so the rule ticked first reads leftmost.
  const selectedRuleIds = useMemo(() => parseComparisonRuleIds(searchParams), [searchParams]);
  // Filters and selection share the URL, so they are always written together —
  // writing either on its own drops the other.
  const setListParams = (nextFilters: MarketingRouterRuleFilters, nextSelectedRuleIds: Array<string>) =>
    setSearchParams(withComparisonSelection(toMarketingRouterRuleFilterParams(nextFilters), nextSelectedRuleIds), {
      replace: true,
    });
  const resetFilters = () => setListParams(EMPTY_MARKETING_ROUTER_RULE_FILTERS, selectedRuleIds);

  const toggleRuleSelection = (ruleId: string) =>
    setListParams(
      filters,
      selectedRuleIds.includes(ruleId)
        ? selectedRuleIds.filter((id) => id !== ruleId)
        : selectedRuleIds.concat([ruleId]),
    );
  const isSelectionFull = selectedRuleIds.length >= MAX_COMPARED_RULES;
  const canCompare = selectedRuleIds.length >= MIN_COMPARED_RULES;
  // A rule stays selected when a filter hides it, so Compare would otherwise
  // quietly include rules the operator can no longer see.
  const hiddenSelectedCount = useMemo(
    () => selectedRuleIds.filter((ruleId) => !shownRules.some((rule) => rule.id === ruleId)).length,
    [selectedRuleIds, shownRules],
  );

  return (
    <>
      <Helmet>
        <title>Marketing / Router Rules</title>
      </Helmet>
      <Container>
        <PageHeader
          title="Marketing Router Rules"
          titleActions={
            <Tabs value={tab} aria-label="Marketing Router Rules views" sx={{ ml: 2, minHeight: 40 }}>
              <Tab label="Rules" value="rules" component={Link} to={tabHref('rules')} sx={{ minHeight: 40 }} />
              <Tab
                label="Resolve a URL"
                value="resolve"
                component={Link}
                to={tabHref('resolve')}
                sx={{ minHeight: 40 }}
              />
            </Tabs>
          }
        >
          <Stack direction="row" spacing={1}>
            <Button variant="text" disabled={!canCompare} onClick={() => navigate(toComparisonPath(selectedRuleIds))}>
              Compare results{selectedRuleIds.length > 0 ? ` (${selectedRuleIds.length})` : ''}
            </Button>
            <Button component={Link} to="/marketing/marketing-router-timeline" variant="text">
              Timeline view
            </Button>
            <Button component={Link} to="/marketing/marketing-router-rules/create" variant="contained" color="primary">
              Create rule
            </Button>
          </Stack>
        </PageHeader>
        {tab === 'resolve' ? <MarketingRouterResolverTab tenant={tenant} /> : null}
        <Card sx={{ display: tab === 'rules' ? undefined : 'none' }}>
          <CardContent>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6">Rules</Typography>
              <Typography color="text.secondary" variant="body2">
                Rules for the current tenant.
              </Typography>
            </Stack>
            <MarketingRouterRulesFilterBar
              filters={filters}
              onChange={(next) => setListParams(next, selectedRuleIds)}
              onReset={resetFilters}
              regionCodes={regionCodes}
              shownCount={shownRules.length}
              totalCount={sortedRules.length}
            />
            {hiddenSelectedCount > 0 && !rulesQuery.isLoading ? (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setListParams(filters, [])}>
                    Clear selection
                  </Button>
                }
              >
                {hiddenSelectedCount} selected {hiddenSelectedCount === 1 ? 'rule is' : 'rules are'} not shown here, and
                {hiddenSelectedCount === 1 ? ' it is' : ' they are'} still included in a comparison.
              </Alert>
            ) : null}
            {rulesQuery.isLoading ? <Typography color="text.secondary">Loading rules…</Typography> : null}
            {rulesQuery.isError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {rulesQuery.error instanceof Error
                  ? rulesQuery.error.message
                  : 'Failed to load marketing router rules.'}
              </Alert>
            ) : null}
            <Table aria-label="Marketing Router Rules">
              <MarketingRouterRuleTableHead />
              <TableBody>
                {shownRules.map((rule) => {
                  const isSelected = selectedRuleIds.includes(rule.id);

                  return (
                    <MarketingRouterRuleTableRow
                      key={rule.id}
                      rule={rule}
                      tenant={tenant}
                      today={today}
                      isExpanded={expandedRuleId === rule.id}
                      onToggleExpanded={() => setExpandedRuleId(expandedRuleId === rule.id ? null : rule.id)}
                      selection={{
                        isSelected,
                        // A comparison only reads with a handful of rules, so
                        // the cap is enforced where it is understandable —
                        // on the rules still unticked.
                        disabled: !isSelected && isSelectionFull,
                        onToggle: () => toggleRuleSelection(rule.id),
                      }}
                    />
                  );
                })}
                {shownRules.length === 0 && !rulesQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={MARKETING_ROUTER_RULE_TABLE_COLUMNS}>
                      {hasFilters && sortedRules.length > 0 ? (
                        <Typography color="text.secondary">
                          No rules match these filters.{' '}
                          <MuiLink component="button" type="button" underline="hover" onClick={resetFilters}>
                            Reset filters
                          </MuiLink>
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">No marketing router rules found.</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
